import React, { useState } from 'react';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarQuickFilter,
  DataGridProps,
  GridFilterPanel,
  GridPaginationInitialState,
  GridFilterModel,
  GridPagination,
  useGridApiContext,
} from '@mui/x-data-grid';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FilterAltOff, FilterAlt, Refresh } from '@mui/icons-material';
import { Notifier } from '../../system/services/notificationManager';
import { useIsSmallScreen } from '../../system/SystemMediaQueriesProvider';

export interface DataTableProps extends DataGridProps {
  rows: Array<{}>;
  columns: Array<GridColDef>;
  loading?: boolean;
  pageSize?: number;
  pageSizeOptions?: Array<number | { label: string; value: number }>;
  getRowId?: any;
  apiRef?: any;
  hideFooter?: boolean;
  initialState?: object;
  columnVisibilityModel?: {};
  filterModel?: GridFilterModel;
  sxProps?: {};
  hideFilterSorting?: boolean;
  hideFilterButton?: boolean;
  hideTextSearch?: boolean;
  additionalProps?: Partial<DataGridProps>;
  customToolbarElement?: React.ReactElement;
  suppressAutoPageSize?: boolean;
  showRefreshButton?: boolean;
  onRefresh?: () => Promise<unknown>;
}

// TODO: Calvin: we don't seem to use the hide filter sorting option.
//  Calvin, do we need this any longer? It seems to have been meant
//  to have been set externally via query parameters on certain screens?
function CustomToolbar(
  customToolbarElement?: React.ReactElement,
  hideFilterSorting?: boolean,
  hideFilterButton?: boolean,
  hideTextSearch?: boolean,
) {
  const [hide, toggleHide] = React.useState(true);
  const theme = useTheme();
  const commonStyles = {
    color: theme.palette.secondary.dark,
    padding: '10px',
  };
  const isSmall = useIsSmallScreen();
  return !hideFilterSorting ? (
    <GridToolbarContainer sx={{ gap: 0 }}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-around"
        wrap={isSmall ? 'wrap' : 'nowrap'}
      >
        {!hideTextSearch ? (
          <Grid
            item
            xs={hideFilterButton ? 11 : 10}
            md={hideFilterButton ? 12 : 11}
            sx={{
              flexGrow: 1,
              maxWidth: '80%',
            }}
          >
            <GridToolbarQuickFilter sx={{ ...commonStyles, width: '100%' }} />
          </Grid>
        ) : null}
        {!hideFilterButton && (
          <Grid item xs="auto">
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Tooltip title="Filter Button">
                <IconButton
                  onClick={() => {
                    if (hide) {
                      toggleHide(false);
                    } else {
                      toggleHide(true);
                    }
                  }}
                  aria-label="filter button"
                  sx={{
                    justifyContent: 'center',
                    backgroundColor: 'lightGray',
                    borderRadius: '4px',
                    width: '100%',
                    '&.MuiButtonBase-root:hover': {
                      bgcolor: 'lightblue',
                      '& .MuiSvgIcon-root': {
                        color: 'white', // Change the color of the icon on hover
                      },
                    },
                  }}
                >
                  {!hide ? (
                    <FilterAltOff fontSize="medium" />
                  ) : (
                    <FilterAlt fontSize="medium" />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          </Grid>
        )}
        {customToolbarElement ? (
          <Grid
            item
            xs={isSmall ? 12 : 'auto'}
            sx={{
              mb: isSmall ? 1 : 0,
              mt: 0,
              pl: isSmall ? 0 : 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              {customToolbarElement}
            </div>
          </Grid>
        ) : null}
        {hide ? null : (
          <>
            <Grid item xs={6}>
              <GridToolbarColumnsButton sx={commonStyles} />
            </Grid>
          </>
        )}
      </Grid>
      <Grid container>
        {!hide ? (
          <Grid item xs={12}>
            <div style={{ maxWidth: isSmall ? 'calc(100vw - 18px)' : 'unset' }}>
              <GridFilterPanel />
            </div>
          </Grid>
        ) : null}
      </Grid>
    </GridToolbarContainer>
  ) : null;
}

// Custom pagination component that fixes the display bug when pageSize is -1
function CustomPagination(props: any) {
  const apiRef = useGridApiContext();
  const paginationModel = apiRef.current.state.pagination.paginationModel;
  const rowCount = apiRef.current.state.pagination.rowCount;
  const pageSize = paginationModel.pageSize;

  // Fix the display when pageSize is -1 (show all)
  if (pageSize === -1 && rowCount > 0) {
    // Override the pagination label to show "1-{rowCount}" instead of the incorrect range
    const fixedProps = {
      ...props,
      labelDisplayedRows: () => `1–${rowCount} of ${rowCount}`,
    };
    return <GridPagination {...fixedProps} />;
  }

  return <GridPagination {...props} />;
}

export default function DataTable(props: DataTableProps) {
  const {
    showCellVerticalBorder,
    rows,
    columns,
    loading,
    apiRef,
    getRowId,
    initialState,
    filterModel,
    columnVisibilityModel,
    additionalProps,
    hideFooter,
    hideFilterSorting,
    hideFilterButton,
    hideTextSearch,
    suppressAutoPageSize,
    customToolbarElement,
    showRefreshButton,
    sxProps,
    onRefresh,
    slots: propsSlots,
    ...rest
  } = props;

  // Extract slots from additionalProps to avoid conflicts
  const additionalSlots = additionalProps?.slots;
  const cleanAdditionalProps = { ...additionalProps };
  delete (cleanAdditionalProps as any).slots;

  // Create merged slots object
  const mergedSlots = {
    ...(hideFooter
      ? {
          toolbar: () =>
            CustomToolbar(
              customToolbarElement || <div></div>,
              hideFilterSorting,
              hideFilterButton,
              hideTextSearch || loading,
            ),
          footer: () => <div></div>,
        }
      : {
          toolbar: () =>
            CustomToolbar(
              customToolbarElement || <div></div>,
              hideFilterSorting,
              hideFilterButton,
              hideTextSearch || loading,
            ),
          pagination: CustomPagination,
        }),
    ...additionalSlots,
    ...propsSlots,
  };
  const pageSize: number = props.pageSize ?? 50;
  const theme = useTheme();
  const isSmall = useIsSmallScreen();
  // On small screens, always display the footer. On large screens, display it unless `hideFooter` is explicitly set
  const hideTableFooter = !isSmall && hideFooter;
  // Disable pagination if the table footer with pagination controls is hidden
  const pagination: GridPaginationInitialState | undefined = hideTableFooter
    ? undefined
    : { paginationModel: { pageSize, page: 0 } };

  const [refreshing, setRefreshing] = useState(false);

  // Refresh table data
  async function handleRefresh() {
    if (onRefresh) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        Notifier.error('Error refreshing data');
        console.error(error);
      } finally {
        setRefreshing(false);
      }
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        position: 'relative',
        flexDirection: 'column',
        alignItems: 'stretch',
        flex: 1,
      }}
    >
      {showRefreshButton && (
        <div
          style={
            // On mobile, display the button in the top right, outside the table.
            // On desktop, position it inside the table in the top right, inline with the filter button.
            isSmall
              ? { alignSelf: 'flex-end' }
              : { position: 'absolute', top: 5, right: 10, zIndex: 100 }
          }
        >
          <IconButton
            sx={{ color: theme.palette.secondary.dark }}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <Refresh />
          </IconButton>
        </div>
      )}
      <DataGrid
        sx={{
          '& .MuiDataGrid-row:hover': {
            color: theme.palette.secondary.main,
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.secondary.dark,
          },
          '& .MuiDataGrid-border:hover': {
            color: theme.palette.secondary.main,
          },
          '& .MuiDataGrid-columnHeader': {
            color: theme.palette.secondary.dark,
          },
          '& .MuiDataGrid-columnHeaders': {
            boxShadow: '0px 6px 6px 0px rgba(0,0,0,0.12)',
            // borderBottom: '0px',
          },
          '& .css-12zdf0-MuiFormControl-root-MuiTextField-root-MuiDataGrid-toolbarQuickFilter':
            {
              padding: 0,
              paddingTop: '10px',
              paddingRight: '10px',
              paddingLeft: '10px',
            },
          '& .MuiDataGrid-cell': {
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '0px',
            fontSize: '14px',
            color: additionalProps?.getRowClassName ? '' : 'rgba(0, 0, 0, 0.6)',
          },
          '& .MuiDataGrid-overlayWrapper': {
            height: '200px',
            width: '100%',
          },
          // Sticky pagination controls for better UX
          '& .MuiDataGrid-footerContainer': {
            position: 'sticky',
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
          },
          ...sxProps,
        }}
        {...cleanAdditionalProps}
        {...rest}
        slots={mergedSlots}
        hideFooter={hideTableFooter}
        showCellVerticalBorder={showCellVerticalBorder ?? true}
        initialState={{
          pagination,
          ...initialState,
        }}
        autoPageSize={isSmall && !suppressAutoPageSize}
        getRowId={getRowId}
        pageSizeOptions={props.pageSizeOptions ?? [5, 10, 25, 50]}
        apiRef={apiRef}
        filterModel={filterModel}
        columnVisibilityModel={columnVisibilityModel}
        rows={rows}
        disableColumnSelector={true}
        columns={columns}
        paginationMode="client"
        loading={loading}
      />
    </div>
  );
}
